<template>
  <div>
    <page-header title="系统管理" description="管理公开舆情的数据源、平台策略、任务、接口和运行日志"/>
    <el-tabs v-model="tab" v-loading="loading" element-loading-text="正在加载系统配置…">
      <el-tab-pane label="基础配置" name="basic">
        <el-card><basic-settings :value="basic" :saving="savingBasic" @save="saveBasic"/></el-card>
      </el-tab-pane>
      <el-tab-pane label="数据源" name="source">
        <el-card>
          <data-source-settings
            :value="tikhub"
            :operator-value="tikhubOperator"
            :operator-error="tikhubOperatorError"
            :saving="savingKey"
            :deleting="deletingKey"
            :operator-saving="savingOperatorKey"
            :operator-deleting="deletingOperatorKey"
            @save-key="saveKey"
            @delete-key="deleteKey"
            @save-operator-key="saveOperatorKey"
            @delete-operator-key="deleteOperatorKey"
            @manage-operator="operatorManagerVisible = true"
          />
        </el-card>
      </el-tab-pane>
      <el-tab-pane label="作品监测账号" name="creator-account">
        <el-card><creator-account-settings/></el-card>
      </el-tab-pane>
      <el-tab-pane label="公开舆情平台" name="policy">
        <el-card>
          <platform-policy
            :value="policies"
            :readiness="readiness"
            :endpoints="endpoints"
            :verifying="verifyingPlatform"
            :saving="savingPolicy"
            @save="savePolicy"
            @verify="verifyPlatform"
          />
        </el-card>
      </el-tab-pane>
      <el-tab-pane label="接口配置" name="endpoint">
        <el-card><endpoint-manager @changed="refreshEndpoints"/></el-card>
      </el-tab-pane>
      <el-tab-pane label="任务中心" name="task">
        <el-card><task-center v-if="tab === 'task'"/></el-card>
      </el-tab-pane>
      <el-tab-pane label="日志中心" name="log">
        <el-card>
          <el-table :data="logs" empty-text="暂无操作日志">
            <el-table-column prop="created_at" label="时间"/>
            <el-table-column prop="log_type" label="类型"/>
            <el-table-column prop="action" label="动作"/>
            <el-table-column prop="summary" label="说明"/>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      title="TikHub 运营授权管理"
      :visible.sync="operatorManagerVisible"
      fullscreen
      append-to-body
    >
      <tik-hub-operator-settings v-if="operatorManagerVisible"/>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import BasicSettings from './BasicSettings.vue'
import DataSourceSettings from './DataSourceSettings.vue'
import TikHubOperatorSettings from './TikHubOperatorSettings.vue'
import PlatformPolicy from './PlatformPolicy.vue'
import EndpointManager from './EndpointManager.vue'
import CreatorAccountSettings from './CreatorAccountSettings.vue'
import TaskCenter from './TaskCenter.vue'

export default {
  components: { PageHeader, BasicSettings, DataSourceSettings, TikHubOperatorSettings, PlatformPolicy, EndpointManager, CreatorAccountSettings, TaskCenter },
  data () {
    const requestedTab = this.$route.query.tab
    return {
      tab: ['basic', 'source', 'creator-account', 'policy', 'endpoint', 'task', 'log']
        .includes(requestedTab) ? requestedTab : (requestedTab === 'operator' ? 'source' : 'basic'),
      operatorManagerVisible: requestedTab === 'operator',
      basic: {},
      tikhub: {},
      tikhubOperator: {},
      tikhubOperatorError: '',
      policies: [],
      readiness: [],
      endpoints: [],
      logs: [],
      verifyingPlatform: '',
      loading: false,
      savingBasic: false,
      savingPolicy: '',
      savingKey: false,
      deletingKey: false,
      savingOperatorKey: false,
      deletingOperatorKey: false
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      if (this.loading) return
      this.loading = true
      this.tikhubOperatorError = ''
      try {
        const operatorStatus = yuqingApi.get('settings/tikhub/operator').catch(error => {
          this.tikhubOperatorError = error.message || '运营代理配置状态读取失败'
          return {}
        })
        const [basic, tikhub, tikhubOperator, policies, readiness, endpoints, logs] = await Promise.all([
          yuqingApi.get('system/basic-settings'),
          yuqingApi.get('settings/tikhub'),
          operatorStatus,
          yuqingApi.get('system/platform-policies'),
          yuqingApi.get('platform-readiness'),
          yuqingApi.get('endpoints'),
          yuqingApi.get('system/logs?limit=200')
        ])
        this.basic = basic
        this.tikhub = tikhub
        this.tikhubOperator = tikhubOperator
        this.policies = policies.policies || []
        this.readiness = readiness.readiness || []
        this.endpoints = endpoints.endpoints || []
        this.logs = logs.logs || []
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.loading = false
      }
    },
    async refreshReadiness () {
      const result = await yuqingApi.get('platform-readiness')
      this.readiness = result.readiness || []
    },
    async refreshLogs () {
      try {
        this.logs = (await yuqingApi.get('system/logs?limit=200')).logs || []
      } catch (error) {
        this.$message.error(error.message || '操作日志刷新失败')
      }
    },
    async refreshEndpoints () {
      try {
        this.endpoints = (await yuqingApi.get('endpoints')).endpoints || []
        await this.refreshLogs()
      } catch (error) {
        this.$message.error(error.message || '接口状态刷新失败')
      }
    },
    async verifyPlatform (platform) {
      if (this.verifyingPlatform) return
      this.verifyingPlatform = platform.platform
      try {
        await yuqingApi.post(`platforms/${encodeURIComponent(platform.platform)}/smoke`, {})
        await this.refreshReadiness()
        const readiness = this.readiness.find(item => item.platform === platform.platform)
        if (readiness && readiness.search_ready) {
          this.$message.success(`${platform.display_name}验证通过，现在可以开启自动舆情`)
          return
        }
        const errors = ((readiness && readiness.checks) || [])
          .filter(check => !check.passed && check.error_summary)
          .map(check => check.error_summary)
        const detail = Array.from(new Set(errors)).join('；')
        this.$message.error(detail || `${platform.display_name}验证未通过，请检查数据源配置`)
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.verifyingPlatform = ''
      }
    },
    async saveBasic (value) {
      if (this.savingBasic) return
      this.savingBasic = true
      try {
        this.basic = await yuqingApi.put('system/basic-settings', value)
        this.$message.success('基础配置已保存')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.savingBasic = false
      }
    },
    async savePolicy (value) {
      if (this.savingPolicy) return
      this.savingPolicy = value.platform
      try {
        await yuqingApi.put('system/platform-policies', value)
        this.$message.success('平台策略已保存')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.savingPolicy = ''
      }
    },
    async saveKey (key) {
      if (this.savingKey || this.deletingKey) return
      if (!key) return this.$message.warning('请输入 TikHub API Key')
      this.savingKey = true
      try {
        await yuqingApi.put('settings/tikhub', { api_key: key })
        this.$message.success('公开数据采集凭证已保存')
        await this.load()
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.savingKey = false
      }
    },
    async deleteKey () {
      if (this.savingKey || this.deletingKey) return
      this.deletingKey = true
      try {
        await yuqingApi.delete('settings/tikhub')
        this.$message.success('公开数据采集凭证已删除')
        await this.load()
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.deletingKey = false
      }
    },
    async saveOperatorKey (key) {
      if (this.savingOperatorKey || this.deletingOperatorKey) return
      if (!key) return this.$message.warning('请输入运营代理 TikHub API Key')
      this.savingOperatorKey = true
      try {
        this.tikhubOperator = await yuqingApi.put(
          'settings/tikhub/operator',
          { api_key: key }
        )
        this.tikhubOperatorError = ''
        this.$message.success('Codex 运营代理凭证已保存并生效')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.savingOperatorKey = false
      }
    },
    async deleteOperatorKey () {
      if (this.savingOperatorKey || this.deletingOperatorKey) return
      this.deletingOperatorKey = true
      try {
        this.tikhubOperator = await yuqingApi.delete('settings/tikhub/operator')
        this.tikhubOperatorError = ''
        const fallback = this.tikhubOperator.source === 'SERVER_CONFIG'
        this.$message.success(fallback
          ? '页面凭证已删除，当前已回退到服务器配置'
          : 'Codex 运营代理凭证已删除')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.deletingOperatorKey = false
      }
    }
  }
}
</script>
